import min from 'lodash/min'
import orderBy from 'lodash/orderBy'

import {
  CatalogBrand,
  FilterCriteria,
  PaginatedMenuItemsQuery,
  PaginatedMenusBrandFilter,
  PaginatedMenusSortCriteria,
  PaginatedMenusSortField,
  PaginatedMenusSortOrder
} from 'src/apollo/onlineOrdering'

import { matchByIdOrName } from './filterUtils'

type PaginatedMenus = PaginatedMenuItemsQuery['paginatedMenuItems']
export type MenuItem = NonNullable<PaginatedMenus['items']>[number]
export type DoProduct = NonNullable<PaginatedMenus['products']>[number]

const minPrice = (prices: ReadonlyArray<number> | null | undefined): number =>
  min(prices) ?? Infinity

const matchesTags = (
  itemTagGuids: ReadonlyArray<string | null | undefined>,
  criteriaTags: FilterCriteria['tags']
): boolean => {
  if (!criteriaTags || criteriaTags.length === 0) return true
  const tagSet = new Set(
    itemTagGuids.filter((guid): guid is string => Boolean(guid))
  )
  return criteriaTags.some((tag) => tag.guid != null && tagSet.has(tag.guid))
}

const matchesPriceRange = (
  price: number,
  range: FilterCriteria['priceRange']
): boolean => !range || (price >= range.min && price <= range.max)

type ItemCustomAttribute = NonNullable<
  NonNullable<MenuItem['catalogProductInfo']>['customAttributes']
>[number]

const matchesBrands = (
  itemBrand: CatalogBrand | null | undefined,
  criteriaBrands: FilterCriteria['brands']
): boolean => {
  if (!criteriaBrands || criteriaBrands.length === 0) return true
  if (!itemBrand) return false
  return criteriaBrands.some((filter: PaginatedMenusBrandFilter) =>
    matchByIdOrName(filter, itemBrand)
  )
}

const matchesCountriesOfOrigin = (
  itemCodes: ReadonlyArray<string> | null | undefined,
  criteriaCountries: FilterCriteria['countriesOfOrigin']
): boolean => {
  if (!criteriaCountries || criteriaCountries.countryCodes.length === 0)
    return true
  if (!itemCodes || itemCodes.length === 0) return false
  const filterSet = new Set(
    criteriaCountries.countryCodes.map((c) => c.toUpperCase())
  )
  return itemCodes.some((code) => filterSet.has(code.toUpperCase()))
}

// AND across attributes, OR within values. Items without catalogProductInfo (non-retail) are filtered out when attribute filters is active
export const matchesCustomAttributes = (
  itemAttrs: Array<ItemCustomAttribute> | null | undefined,
  criteriaAttrs: FilterCriteria['customAttributes']
): boolean => {
  if (!criteriaAttrs || criteriaAttrs.length === 0) return true
  if (!itemAttrs) return false
  return criteriaAttrs.every((filterAttr) => {
    const attribute = itemAttrs.find((itemAttr) =>
      matchByIdOrName(itemAttr, filterAttr)
    )
    if (!attribute) return false
    return filterAttr.values.some((filterValue) =>
      attribute.values.some((attrValue) =>
        matchByIdOrName(attrValue, filterValue)
      )
    )
  })
}

export const filterItem = (
  item: MenuItem,
  criteria: FilterCriteria,
  hideOutOfStock: boolean
): boolean => {
  if (hideOutOfStock && item.outOfStock) {
    return false
  }

  if (!matchesTags(item.itemTags?.map((t) => t.guid) ?? [], criteria.tags)) {
    return false
  }

  if (!matchesPriceRange(minPrice(item.prices), criteria.priceRange)) {
    return false
  }

  // Product option filters do not apply to flat MenuItem (no availableOptions
  // field on MenuItemFragment). The server only applies them to DoProduct.

  if (
    !matchesCustomAttributes(
      item.catalogProductInfo?.customAttributes,
      criteria.customAttributes
    )
  ) {
    return false
  }

  if (!matchesBrands(item.catalogProductInfo?.brand, criteria.brands)) {
    return false
  }

  if (
    !matchesCountriesOfOrigin(
      item.countriesOfOrigin?.countryCodes,
      criteria.countriesOfOrigin
    )
  ) {
    return false
  }

  return true
}

export const filterProduct = (
  product: DoProduct,
  criteria: FilterCriteria,
  hideOutOfStock: boolean
): boolean => {
  if (hideOutOfStock && product.outOfStock) return false

  // ProductOptions stay at product level: the variant payload doesn't include
  // catalogProductInfo.selectedOptions, so we can only check whether the
  // product exposes the requested option dimension at all.
  if (
    criteria.productOptions &&
    criteria.productOptions.length > 0 &&
    !criteria.productOptions.every((filterOption) => {
      const productOption = product.availableOptions?.find(
        (o) => o.name === filterOption.name
      )
      if (!productOption) return false
      const valueNames = new Set(productOption.values.map((v) => v.name))
      return filterOption.values.some((v) => valueNames.has(v))
    })
  ) {
    return false
  }

  // Tags / price / out-of-stock / attributes are delegated to variants: a product matches
  // if at least one variant matches (mirrors server FilterMatcher.matchesProduct).
  return (
    product.variants?.some((v) => filterItem(v, criteria, hideOutOfStock)) ??
    false
  )
}

type SortableEntry = { name?: string | null; prices: ReadonlyArray<number> }

export const sortCatalogEntries = <T extends SortableEntry>(
  entries: ReadonlyArray<T>,
  sort: ReadonlyArray<PaginatedMenusSortCriteria> | null | undefined
): T[] => {
  if (!sort || sort.length === 0) return entries.slice()

  const iteratees = sort.map((criterion) => {
    switch (criterion.field) {
      case PaginatedMenusSortField.Name:
        return (e: T) => e.name?.toLowerCase() ?? ''
      case PaginatedMenusSortField.Price:
        return (e: T) => minPrice(e.prices)
      default:
        return () => 0
    }
  })

  const orders = sort.map(
    (c) =>
      (c.order ?? PaginatedMenusSortOrder.Asc).toLowerCase() as 'asc' | 'desc'
  )

  return orderBy(entries, iteratees, orders)
}
